import type { EmbeddedPromptOut } from '@repo/api-codegen';
import type { Ref } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { VirtuosoHandle, VirtuosoProps } from 'react-virtuoso';
import { Virtuoso } from 'react-virtuoso';
import { MessageGroup } from './Messages/MessageGroup';

type AIChatConversationProps = {
	messages?: EmbeddedPromptOut[];
	onRetry?: (messageId: string) => void;
	handle: Ref<VirtuosoHandle>;
	onScrollToBottom: (align: 'start' | 'end') => void;
};

export function AIChatConversation({
	messages = [],
	onRetry,
	handle,
	onScrollToBottom,
	...props
}: AIChatConversationProps &
	VirtuosoProps<EmbeddedPromptOut, EmbeddedPromptOut>) {
	const refLastMessagePendingOrRunning = useRef<string | null>(null);

	useEffect(() => {
		const lastMessage = messages?.[messages.length - 1];
		if (
			lastMessage &&
			['pending', 'running'].includes(lastMessage.status ?? 'completed')
		) {
			if (refLastMessagePendingOrRunning.current !== lastMessage.id) {
				refLastMessagePendingOrRunning.current = lastMessage.id;
				setTimeout(() => {
					onScrollToBottom('start');
				}, 100);
			}
		}
	}, [messages, onScrollToBottom]);

	const renderVirtuosoItem = useCallback(
		(index: number, message: EmbeddedPromptOut) => (
			<MessageGroup
				key={message.id}
				message={message}
				onRetry={onRetry}
				shouldIncreaseHeight={
					message.id === refLastMessagePendingOrRunning.current
				}
				isLastMessage={index === messages.length - 1}
			/>
		),
		[messages.length, onRetry]
	);

	return (
		<Virtuoso
			ref={handle}
			totalCount={messages.length}
			data={messages}
			itemContent={renderVirtuosoItem}
			className="ai-chat-scroll-container"
			increaseViewportBy={window.innerHeight * 2}
			{...props}
		/>
	);
}
